/**
 * Kuutio Vakioskenaariot - Variable Descriptions
 *
 * These descriptions provide context for AI when generating suggestions
 * within a scenario/variable context. Used by RAG + AI endpoints.
 *
 * Source: data/vakioskenaariot.md
 */

import { normalizeInstanceLanguage } from '../../../../packages/shared/instanceLanguage.js';

export const SCENARIO_DESCRIPTIONS = {
  // Ilmasto (Climate)
  ilmasto: {
    title: 'Ilmasto',
    description: 'Ilmastonmuutoksen vaikutukset Suomeen vuoteen 2050 mennessä.',
    variables: {
      'keskilämpötilan muutos': 'Suomen vuotuinen keskilämpötila nousee 2-4°C. Vaikuttaa kasvukausiin, energiankulutukseen, ekosysteemeihin ja rakennusten lämmitystarpeeseen.',
      'veden kierrot': 'Sateisuuden, kuivuuskausien ja vesistöjen muutokset. Talvisateet lisääntyvät, kesäkuivuus yleistyy. Vaikuttaa maatalouteen, vesihuoltoon ja tulvariskeihin.',
      'sään ääri-ilmiöt': 'Myrskyjen, helleaaltojen, rankkasateiden ja muiden ääri-ilmiöiden yleistyminen. Infrastruktuurin kestävyys ja varautuminen korostuvat.',
      'biologiset vaikutukset': 'Lajiston muutokset, tuholaiset, taudit ja ekosysteemien sopeutuminen. Uudet vieraslajit, allergiakauden piteneminen, metsien terveys.',
      'globaalit muutokset': 'Maailmanlaajuiset ilmastovaikutukset jotka heijastuvat Suomeen: ilmastopakolaisuus, ruokaturva, toimitusketjut, raaka-aineiden saatavuus.',
      'heijastevaikutukset': 'Epäsuorat vaikutukset toimitusketjuihin, kauppaan ja kansainvälisiin suhteisiin. Muiden maiden ilmastotoimien vaikutus Suomen talouteen.'
    }
  },

  // Demokratia (Democracy)
  demokratia: {
    title: 'Demokratia',
    description: 'Yhteiskunnallisen päätöksenteon ja hallintorakenteiden tulevaisuus.',
    variables: {
      'talous ja geopolitiikka': 'Globaalin talouden ja valtasuhteiden muutokset. Kauppasodat, sanktiot, liittoumat, EU:n kehitys ja Suomen asema kansainvälisessä järjestelmässä.',
      'teknologinen muutos': 'Digitalisaation, tekoälyn ja automaation vaikutukset demokratiaan. Disinformaatio, algoritmit, sähköinen äänestys, kansalaisten osallistuminen.',
      'ideologinen kenttä': 'Poliittisten aatteiden, arvojen ja puoluekentän muutokset. Polarisaatio, identiteettipolitiikka, uudet liikkeet ja puolueiden uudistuminen.',
      'muuttoliike': 'Maahanmuuton ja maastamuuton vaikutukset yhteiskuntaan. Työmarkkinat, kulttuuri, integraatio, asenteet ja monikulttuurisuus.',
      'päätöksenteon rakenteet': 'EU:n, kansallisen ja paikallisen tason päätöksenteon kehitys. Kuntauudistukset, aluehallinto, demokratian uudet muodot.',
      'ilmastopolitiikka': 'Ilmastotoimien vaikutukset politiikkaan ja sääntelyyn. Hiilineutraaliustavoitteet, energiasiirtymä, kansalaisten arki ja oikeudenmukaisuus.'
    }
  },

  // Väestö (Population)
  väestö: {
    title: 'Väestö',
    description: 'Suomen väestörakenteen muutokset ja niiden vaikutukset.',
    variables: {
      'nettomaahanmuuton vuotuinen taso': 'Maahanmuuton ja maastamuuton erotus vuosittain. Vaikuttaa työvoimaan, kulttuuriin, palveluihin ja huoltosuhteeseen. Tavoitetaso 40 000+/vuosi.',
      'nettomaahanmuutto': 'Maahanmuuton ja maastamuuton erotus vuosittain. Vaikuttaa työvoimaan, kulttuuriin, palveluihin ja huoltosuhteeseen.',
      'työikäisen väestön kehitys': 'Työikäisten määrän kehitys. Työvoimapula, eläköityminen, osaamistarpeet, talouden kantokyky ja verotulojen kehitys.',
      'työikäinen väestö': 'Työikäisten (18-64v) määrän kehitys. Työvoimapula, eläköityminen, osaamistarpeet, talouden kantokyky.',
      'vanhushuoltosuhde': 'Ikääntyneiden määrä suhteessa työikäisiin. Hoivapalvelut, eläkkeet, julkistalouden kestävyys, sukupolvien välinen oikeudenmukaisuus.',
      'julkisen talouden kestävyysvaje': 'Pitkän aikavälin epätasapaino tulojen ja menojen välillä. Velkaantuminen, palvelutaso, verotus ja hyvinvointivaltion tulevaisuus.',
      'syntyvyys': 'Syntyvyysluvun kehitys. Vaikuttaa pitkällä aikavälillä väestörakenteeseen ja yhteiskunnan uusiutumiseen.',
      'alueellinen jakautuminen': 'Väestön keskittyminen kaupunkeihin vs. maaseutu. Vaikuttaa palveluihin, asumiseen ja työpaikkoihin.',
      'elinajanodote': 'Odotetun eliniän kehitys. Vaikuttaa terveydenhuoltoon, eläkejärjestelmään ja elämänlaatuun.'
    }
  },

  // Teknologia (Technology)
  teknologia: {
    title: 'Teknologia',
    description: 'Teknologisen kehityksen vaikutukset yhteiskuntaan ja arkeen.',
    variables: {
      'toimijuus': 'Ihmisen ja teknologian välinen suhde. Kuka ohjaa teknologiaa - ihminen vai algoritmi? Autonomia, riippuvuus, digitaaliset kansalaistaidot.',
      'etiikka ja säätely': 'Teknologian eettinen käyttö ja sääntely. Tekoälyn läpinäkyvyys, yksityisyys, vastuu, EU:n tekoälysäädökset ja kansalliset linjaukset.',
      'valta- ja omistusrakenteet': 'Datan ja alustojen omistus ja valta. Teknologiajätit, kilpailu, digitaalinen suverenitetti, kansallinen omistajuus vs. globaalit alustat.',
      'teknologian saavutettavuus': 'Digitaalisten palveluiden ja teknologian tasapuolinen saatavuus. Digiköyhyys, ikääntyneet, maaseutu vs. kaupunki, eriarvoistuminen.',
      'yhteisöllisyys': 'Teknologian vaikutus sosiaalisiin suhteisiin. Yksinäisyys, virtuaaliyhteisöt, lähiyhteisöt, kasvokkainen vuorovaikutus vs. etäyhteydet.',
      'tekoälyn kehitys': 'AI-järjestelmien yleistyminen ja vaikutukset työhön, päätöksentekoon ja arkeen.',
      'digitalisaatio': 'Palveluiden ja prosessien siirtyminen digitaalisiksi. Vaikuttaa saavutettavuuteen ja tehokkuuteen.',
      'automaation taso': 'Robotiikan ja automaation yleistyminen tuotannossa, palveluissa ja liikenteessä.',
      'kyberturvallisuus': 'Digitaalisten uhkien hallinta, tietosuoja ja kriittisen infrastruktuurin suojaaminen.',
      'datatalous': 'Datan kerääminen, käyttö ja kaupallinen hyödyntäminen. Yksityisyyden ja kilpailun kysymykset.',
      'alustatalous': 'Digitaalisten alustojen (Uber, Airbnb, jne.) vaikutukset työhön, kuluttamiseen ja sääntelyyn.'
    }
  }
};

/** English catalog for AI context (sv workshops fall back here). Keys match FI. */
export const SCENARIO_DESCRIPTIONS_EN = {
  ilmasto: {
    title: 'Climate',
    description: 'Impacts of climate change on Finland through 2050.',
    variables: {
      'keskilämpötilan muutos': 'Finland’s annual mean temperature rises by 2–4°C. Affects growing seasons, energy use, ecosystems, and building heating needs.',
      'veden kierrot': 'Changes in rainfall, drought periods, and water systems. More winter rain, more summer drought. Affects agriculture, water supply, and flood risk.',
      'sään ääri-ilmiöt': 'More storms, heatwaves, heavy rain, and other extremes. Infrastructure resilience and preparedness become critical.',
      'biologiset vaikutukset': 'Species shifts, pests, disease, and ecosystem adaptation. New invasive species, longer allergy seasons, forest health.',
      'globaalit muutokset': 'Global climate impacts that reach Finland: climate migration, food security, supply chains, raw-material availability.',
      'heijastevaikutukset': 'Indirect effects on supply chains, trade, and international relations. How other countries’ climate actions affect Finland’s economy.'
    }
  },
  demokratia: {
    title: 'Democracy',
    description: 'The future of societal decision-making and governance structures.',
    variables: {
      'talous ja geopolitiikka': 'Shifts in the global economy and power relations. Trade wars, sanctions, alliances, EU development, and Finland’s place in the international system.',
      'teknologinen muutos': 'Effects of digitalisation, AI, and automation on democracy. Disinformation, algorithms, electronic voting, citizen participation.',
      'ideologinen kenttä': 'Changes in political ideas, values, and the party landscape. Polarisation, identity politics, new movements, party renewal.',
      'muuttoliike': 'Effects of immigration and emigration on society. Labour markets, culture, integration, attitudes, and multiculturalism.',
      'päätöksenteon rakenteet': 'Development of EU, national, and local decision-making. Municipal reforms, regional administration, new forms of democracy.',
      'ilmastopolitiikka': 'How climate action reshapes politics and regulation. Carbon-neutrality goals, energy transition, everyday life, and fairness.'
    }
  },
  väestö: {
    title: 'Population',
    description: 'Changes in Finland’s population structure and their effects.',
    variables: {
      'nettomaahanmuuton vuotuinen taso': 'Annual net migration (immigration minus emigration). Affects labour supply, culture, services, and dependency ratio. Target ~40,000+/year.',
      'nettomaahanmuutto': 'Annual net migration. Affects labour supply, culture, services, and dependency ratio.',
      'työikäisen väestön kehitys': 'Trends in the working-age population. Labour shortages, retirement, skills needs, economic capacity, and tax revenues.',
      'työikäinen väestö': 'Working-age (18–64) population trends. Labour shortages, retirement, skills needs, economic capacity.',
      'vanhushuoltosuhde': 'Number of older people relative to working-age adults. Care services, pensions, public-finance sustainability, intergenerational fairness.',
      'julkisen talouden kestävyysvaje': 'Long-term imbalance between revenues and spending. Debt, service levels, taxation, and the future of the welfare state.',
      'syntyvyys': 'Fertility trends. Long-term effects on population structure and societal renewal.',
      'alueellinen jakautuminen': 'Population concentrating in cities vs countryside. Affects services, housing, and jobs.',
      'elinajanodote': 'Trends in life expectancy. Affects healthcare, the pension system, and quality of life.'
    }
  },
  teknologia: {
    title: 'Technology',
    description: 'Effects of technological development on society and everyday life.',
    variables: {
      'toimijuus': 'The relationship between humans and technology. Who steers technology — people or algorithms? Autonomy, dependence, digital citizenship skills.',
      'etiikka ja säätely': 'Ethical use and regulation of technology. AI transparency, privacy, accountability, EU AI rules, and national policy.',
      'valta- ja omistusrakenteet': 'Ownership and power over data and platforms. Tech giants, competition, digital sovereignty, national ownership vs global platforms.',
      'teknologian saavutettavuus': 'Equitable access to digital services and technology. Digital poverty, older adults, rural vs urban, inequality.',
      'yhteisöllisyys': 'Technology’s effect on social relationships. Loneliness, virtual communities, local communities, face-to-face vs remote interaction.',
      'tekoälyn kehitys': 'Spread of AI systems and effects on work, decision-making, and daily life.',
      'digitalisaatio': 'Services and processes moving digital. Affects accessibility and efficiency.',
      'automaation taso': 'Spread of robotics and automation in production, services, and transport.',
      'kyberturvallisuus': 'Managing digital threats, data protection, and protecting critical infrastructure.',
      'datatalous': 'Collecting, using, and commercialising data. Privacy and competition questions.',
      'alustatalous': 'Effects of digital platforms (Uber, Airbnb, etc.) on work, consumption, and regulation.'
    }
  }
};

function catalogForLanguage(language) {
  const lang = normalizeInstanceLanguage(language);
  // sv workshops use English AI context until a full SV catalog exists
  return lang === 'fi' ? SCENARIO_DESCRIPTIONS : SCENARIO_DESCRIPTIONS_EN;
}

/**
 * Get scenario context for AI prompts
 * @param {string} scenarioTitle - Title of the scenario (e.g., "Ilmasto", "Demokratia")
 * @param {string} variableTitle - Title of the variable (optional)
 * @param {'fi'|'en'|'sv'|string} [language='fi'] - Instance language for description text
 * @returns {object} Context object with scenario and variable descriptions
 */
export function getScenarioContext(scenarioTitle, variableTitle = null, language = 'fi') {
  if (!scenarioTitle) return null;

  const catalog = catalogForLanguage(language);
  // Normalize scenario title for lookup (keys stay Finnish identifiers)
  const normalizedScenario = scenarioTitle.toLowerCase().trim();

  let scenario = catalog[normalizedScenario];

  if (!scenario) {
    const matchingKey = Object.keys(catalog).find(key =>
      normalizedScenario.includes(key) || key.includes(normalizedScenario)
    );
    if (matchingKey) {
      scenario = catalog[matchingKey];
      console.log(`📝 Matched scenario "${scenarioTitle}" → "${matchingKey}"`);
    }
  }

  // Also try FI key match when looking up EN catalog via Finnish title words
  if (!scenario && catalog !== SCENARIO_DESCRIPTIONS) {
    const fiMatch = Object.keys(SCENARIO_DESCRIPTIONS).find(key =>
      normalizedScenario.includes(key) || key.includes(normalizedScenario)
    );
    if (fiMatch) scenario = catalog[fiMatch];
  }

  if (!scenario) {
    console.warn(`⚠️ Unknown scenario: ${scenarioTitle}`);
    return null;
  }

  const context = {
    scenarioTitle: scenario.title,
    scenarioDescription: scenario.description
  };

  if (variableTitle) {
    const normalizedVariable = variableTitle.toLowerCase().trim();
    const variableDescription = scenario.variables[normalizedVariable];

    if (variableDescription) {
      context.variableTitle = variableTitle;
      context.variableDescription = variableDescription;
    } else {
      const matchingKey = Object.keys(scenario.variables).find(key =>
        key.includes(normalizedVariable) || normalizedVariable.includes(key)
      );
      if (matchingKey) {
        context.variableTitle = matchingKey;
        context.variableDescription = scenario.variables[matchingKey];
      }
    }
  }

  return context;
}

/**
 * Get all variables for a scenario
 * @param {string} scenarioTitle - Title of the scenario
 * @param {'fi'|'en'|'sv'|string} [language='fi']
 * @returns {Array} Array of {title, description} objects
 */
export function getScenarioVariables(scenarioTitle, language = 'fi') {
  if (!scenarioTitle) return [];

  const catalog = catalogForLanguage(language);
  const normalizedScenario = scenarioTitle.toLowerCase().trim();
  const scenario = catalog[normalizedScenario];

  if (!scenario) return [];

  return Object.entries(scenario.variables).map(([title, description]) => ({
    title,
    description
  }));
}

export default SCENARIO_DESCRIPTIONS;
